/**
 * @copyright Copyright 2016-2019 The MathWorks, Inc.
 * @description Dialogs is a factory module which used to create a basic dialog framework
 * @module mw-dialogs/Dialogs
 */
define([
    "dojo/_base/lang",
    "mw-dialogs/view/Dialog",
    "mw-dialogs/enums/RoleEnum",
    "mw-utils/Utils"
], function (lang, Dialog, RoleEnum, Utils) {

    let generateUuid = () => {
        return Utils.generateUuid();
    };
    /**
     * set default options for dialog
     */
    let setDefaultOptions = (options) => {
        options.uuid = generateUuid();
        options.id = options.id ? options.id : 'dialog' + '_' + options.uuid;
        options.className = typeof options.className === 'string' ?
            lang.trim(options.className) : '';
        options.dialogType = options.dialogType === 'modal' ? 'modal' : 'modeless';
        options.hideCloseButton = typeof options.hideCloseButton === 'boolean' ?
            options.hideCloseButton : false;
        options.closeOnEscape = typeof options.closeOnEscape === 'boolean' ?
            options.closeOnEscape : true;
        options.resizable = typeof options.resizable === 'boolean' ?
            options.resizable : false;
        options.draggable = typeof options.draggable === 'boolean' ?
            options.draggable : (options.dialogType === 'modeless');

        return options;
    };

    let dialogs = {

        MODAL: "modal",

        MODELESS: "modeless",

        ROLE: RoleEnum,
        /**
         * @function numberOfOpenDialogs
         *
         * @description returns the number of dialogs open of both types
         *
         * @static
         *
         * @returns {Object} count - returns an object with the count of open modal
         * and modeless dialogs
         *
         * @example
         *  var Dialogs = require("mw-dialogs/Dialogs");
         *  var dialog = Dialogs.createDialog({
         *     dialogType: Dialogs.MODAL
         * });
         *
         * var count = Dialogs.numberOfOpenDialogs();
         * console.log(count.modal); // Number of modal dialogs open
         * console.log(count.modeless);// Number of modeless dialogs open
         */
        numberOfOpenDialogs: Dialog.numberOfOpenDialogs.bind(Dialog),

        /**
         * @function createDialog
         *
         * @description initializes a new instance of the dialog with sets of options
         * supplied by developer
         *
         * @static
         *
         * @returns {Object} dialog - returns the dialog widget
         * @param {Object} [options] - This object can be used to set different options and
         * configuration for the dialog. Currently supported options include following:
         * @Param {String} [options.title] - The title to be shown on dialog.
         * @param {Array} [options.buttons] - Array of button objects to be displayed on the
         * dialog. If this option is not specified, then there is no buttons in the dialog.
         * @param {String} [options.buttons.type] - Type of the button. Please see ButtonENUM for
         * the type of button you could specify.
         * @param {String} [options.buttons.label] - Text displayed on the button
         * @param {String} [options.buttons.data-test-id] - Test id data-tag for the button
         * @param {Object | String} [options.defaultActionButton] - This option
         * determines which button is the default action button which is always mapped to the
         * Enter/Return keys, it could be Button or type of Button.
         * If the developer doesn't want dialog to have a default action, then specify
         * defaultActionButton: ButtonEnum.NO_DEFAULT_ACTION
         * If this option is not specified, then the first commit button will be the default
         * action button.
         * @param {String | Object | domNode} [options.content] - Message or content to be displayed
         * on the dialog. This can be string or a dijit widget. If this is not specified, then
         * there is no content in the dialog content area.
         * @param {Object} [options.position] - An object containing the x and y values of the
         * location to specifies where the dialog should be displayed when dialog is opened.
         * E.g., {x: 0, y: 0}  If this option is not specified, then dialog will position at
         * the center of the window or constraint.
         * @param {String} [options.className] - CSS classes that can be applied to dialog
         * @param {String} [options.data-test-id] - Data tag that can be given to dialog which can
         * be reliable used for applying attribute or for testing
         * @param {String} [options.dialogType='modeless'] - Determines if the dialog is modal or
         * modeless to the application. If this option is not specified, dialog is modeless.
         * @param {String} [options.role= Dialogs.ROLE.DIALOG] - Determines the accessibility role
         * of the dialog. Use Dialog's built in ROLE enum to specify a value. This option is valid
         * only when the dialog type is modal. Please see RoleEnum for the type of role that
         * can be specified.
         * @param {Boolean} [options.draggable] - Determines whether the dialog is movable.
         * @param {domNode} [options.bindingNode] - Specified the DOM Node which constraints the
         * boundary of the dialog. If this option is not specified, then dialog will be
         * constrained within the window.
         * @param {Boolean} [options.resizable=false] - Determines whether dialog itself is
         * resizable by using the affordance.
         * @param {Boolean} [options.hideCloseButton=false] - Determines whether to display the
         * [x] icon in the dialog header or not.
         * @param {Boolean} [options.closeOnEscape=true] - Determines whether the dialog should
         * close when it has focus and the user presses the escape (ESC) key.
         * @param {String | ButtonEnum} [options.specifiedFocusItem] -
         * - The id of the domNode that determines
         * which item in the dialog content area will have focus when the dialog shows up. If
         * this option is not specified or the specifiedFocusItem couldn't be found, the first
         * focusable element in the dialog will get focus when the dialog shows up.
         * E.g., specifiedFocusItem: "exampleId"
         * - Alternatively, a ButtonEnum can also be specified to have intial focus and thus skipping any
         * focusable elements in the dialog content area. If specified button is not found, then
         * the default action or first commit button will be focused.
         * E.g., specifiedFocusItem: ButtonEnum.SAVE
         *
         * @example <caption>Example of create a dialog with various options:</caption>
         *  var Dialogs = require("mw-dialogs/Dialogs");
         *  var ButtonEnum = require("mw-dialogs/ButtonEnum");
         *  var dialog = Dialogs.createDialog({
         *     title: "Data Object Wizard",
         *     dialogType: Dialogs.MODAL,
         *     role: Dialogs.ROLE.ALERT_DIALOG,
         *     draggable: true,
         *     resizable: true,
         *     "data-test-id": "dialog",
         *     content: "Message",
         *     buttons: [
         *         ButtonEnum.HELP,
         *         {
         *             type: ButtonEnum.TYPE.DO_IT,
         *             label: 'Sign up'
         *         },
         *         ButtonEnum.CANCEL
         *     ],
         *     defaultActionButton: ButtonEnum.TYPE.DO_IT
         * });
         *
         * @example <caption>In case of custom buttons, make sure 'data-test-id' is unique</caption>
         * var Dialogs = require("mw-dialogs/Dialogs");
         * var ButtonEnum = require("mw-dialogs/ButtonEnum");
         * var customDetailsButton = {
         *      type: ButtonEnum.TYPE.SPL1,
         *      label: 'Details',
         *      "data-test-id": "DetailsButton"
         *    }
         * var dialog = Dialogs.createDialog({
         *    dialogType: Dialogs.MODAL,
         *    buttons: [
         *       customDetailsButton,
         *       ButtonEnum.HELP,
         *       ButtonEnum.OK
         *    ],
         *    specifiedFocusItem: customDetailsButton
         * });
         *
         * @example <caption>Example of attaching listeners for a button:</caption>
         *  dialog.addButtonEventListener(ButtonEnum.TYPE.DO_IT, function(e) {
         *      console.log("DO IT");
         *  }, true);
         *
         *  dialog.addButtonEventListener(ButtonEnum.CANCEL, function(e) {
         *      console.log("CANCEL");
         *  }, true);
         *
         * @example <caption>Example of destroying a dialog:</caption>
         *  dialog.destroy();
         */
        createDialog (options) {
            options = setDefaultOptions(options);
            return new Dialog(options);
        }
    };

    return dialogs;
});
